import DashboardSkeleton from "./DashboardSkeleton";
import OverviewProjectCharts from "./OverviewProjectCharts";
import { completeMetricYears } from "./metricYears";
import "./MetricCard.css";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { useHistory } from "react-router-dom";
import { WEB_API_DASHBOARD } from "../../env";
import MonthlyReportExport from './MonthlyReportExport';
import { StaticImage } from "../../utils/image";
import ApiLaw from "../../api/law";
import moment from "moment";

// Helper function to format numbers with commas
const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    const num = String(value).replace(/,/g, '');
    if (isNaN(num)) return value;
    return Number(num).toLocaleString('en-US');
};

const PillButton = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${active
            ? "border-sky-500 bg-sky-50 text-sky-600 shadow-sm"
            : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
            }`}
    >
        {label}
    </button>
);

const FilterSelect = ({ label, options, value, onChange, className = "" }) => {
    const normalizedOptions = options.map((option) => (
        typeof option === "string" ? { value: option, label: option } : option
    ));
    const optionValueMap = useMemo(
        () => new Map(normalizedOptions.map((option) => [String(option.value ?? ""), option.value])),
        [normalizedOptions]
    );

    return (
        <label className={`flex flex-col gap-1 text-xs text-slate-500 ${className}`}>
            {label}
            <select
                value={value ?? ""}
                onChange={(event) => onChange(optionValueMap.get(event.target.value))}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none"
            >
                {normalizedOptions.map((option) => (
                    <option key={String(option.value ?? "")} value={String(option.value ?? "")}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
};

export const MetricCard = ({ item }) => {
    const history = useHistory();
    return (
        <div className="dashboard-metric-card rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="shrink-0">{item.icon}</div>
                <span className="dashboard-metric-card__total text-right font-semibold text-slate-900">{formatNumber(item.value)}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-start justify-between gap-2">
                <h3 className="text-sm font-medium text-slate-800">{item.title}</h3>
                {item.unit ? <span className="text-sm text-slate-400 font-semibold">{item.unit}</span> : null}
            </div>

            {item.delta && (
                <div className="mt-3 flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${item.deltaClass}`}>
                        {item.delta}
                    </span>
                    <span className="text-xs text-slate-400">ข้อมูลรายปี</span>
                </div>
            )}

            <div className="mt-4 text-xs text-slate-500">
                <div className="dashboard-metric-card__years divide-x divide-slate-200">
                    {completeMetricYears(item.stats, new Date(), item.fiscalYear).map((stat) => (
                        <div key={stat.label} className="dashboard-metric-card__year">
                            <p className="dashboard-metric-card__number text-slate-800">
                                {formatNumber(stat.value)}
                            </p>
                            <p className="whitespace-nowrap text-[11px] text-slate-600">
                                ปี {stat.label}
                            </p>
                        </div>
                    ))}
                </div>
                {item.link ? (
                    <button
                        type="button"
                        aria-label={`ดูรายละเอียด${item.title}`}
                        onClick={() => {
                            history.push(item.link)
                        }}
                        className="mt-3 ml-auto flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-sky-600 hover:bg-slate-50"
                    >
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/eye-duotone.svg")} />
                    </button>
                ) : null}
            </div>
        </div>
    )
}

const LineChartCard = ({ title, subtitle, labels, series }) => {
    const chartOptions = useMemo(
        () => ({
            chart: {
                id: `line-${title}`,
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
                parentHeightOffset: 0,
            },
            stroke: {
                width: 3,
                curve: "straight",
            },
            colors: series.map((item) => item.color),
            dataLabels: {
                enabled: false,
            },
            grid: {
                borderColor: "#e2e8f0",
                strokeDashArray: 4,
                padding: {
                    left: 0,
                    right: 0,
                },
            },
            xaxis: {
                categories: labels,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: {
                    style: {
                        colors: "#94a3b8",
                        fontSize: "11px",
                    },
                },
            },
            markers: {
                size: 4,
                strokeWidth: 2,
                strokeColors: "#fff",
            },
            yaxis: {
                labels: {
                    formatter: (value) => formatNumber(value),
                    style: {
                        colors: "#94a3b8",
                        fontSize: "11px",
                    },
                },
            },
            legend: { show: false },
            tooltip: {
                theme: "light",
                y: {
                    formatter: (value) => formatNumber(value),
                },
            },
        }),
        [labels, series, title]
    );

    const chartSeries = useMemo(
        () => series.map(({ name, data }) => ({ name, data })),
        [series]
    );

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-medium text-slate-800">{title}</h3>
                    <p className="text-xs text-slate-400">{subtitle}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    {series.map((item) => (
                        <div key={item.name} className="flex items-center gap-1">
                            <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: item.color }}
                            />
                            {item.name}
                        </div>
                    ))}
                </div>
            </div>
            <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="line"
                width="100%"
                height={320}
            />
        </div>
    );
};

const BarChartCard = ({ title, subtitle, labels, values, accent = "sky", barColors = [] }) => {
    const colorMap = {
        sky: "#80CBC4",
        violet: "#5C6Bc0",
    };
    const resolvedColors = barColors.length ? barColors : [colorMap[accent] || colorMap.sky];
    const isDistributed = resolvedColors.length > 1;

    const chartOptions = useMemo(
        () => ({
            chart: {
                type: "bar",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
                parentHeightOffset: 0,
            },
            plotOptions: {
                bar: {
                    borderRadius: 6,
                    columnWidth: "55%",
                    distributed: isDistributed,
                    dataLabels: { position: "top" },
                },
            },
            dataLabels: { enabled: true, offsetY: -20, formatter: formatNumber,
                style: { colors: ["#475569"], fontSize: "11px" }, background: { enabled: false } },
            grid: {
                borderColor: "#e2e8f0",
                strokeDashArray: 4,
                padding: {
                    left: 0,
                    right: 0,
                    top: 24,
                },
            },
            xaxis: {
                categories: labels,
                labels: {
                    style: { colors: "#94a3b8", fontSize: "11px" },
                },
                axisTicks: { show: false },
                axisBorder: { show: false },
            },
            yaxis: {
                labels: {
                    formatter: (value) => formatNumber(value),
                    style: { colors: "#94a3b8", fontSize: "11px" },
                },
            },
            colors: resolvedColors,
            tooltip: {
                theme: "light",
                y: {
                    formatter: (value) => formatNumber(value),
                },
            },
        }),
        [accent, isDistributed, labels, resolvedColors]
    );

    const chartSeries = useMemo(
        () => [
            {
                name: "ความคิดเห็น (ครั้ง)",
                data: values,
            },
        ],
        [values]
    );

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-medium text-slate-800">{title}</h3>
                    <p className="text-xs text-slate-400">{subtitle}</p>
                </div>
            </div>
            <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                width="100%"
                height={320}
            />
        </div>
    );
};

const DonutChartCard = ({ title, subtitle, series, remarks }) => {
    const chartOptions = useMemo(
        () => ({
            chart: {
                type: "donut",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
                parentHeightOffset: 0,
            },
            labels: series.map((slice) => slice.label),
            colors: series.map((slice) => slice.color),
            dataLabels: {
                enabled: false,
            },
            stroke: {
                show: true,
                width: 2,
                colors: ["#fff"],
            },
            legend: {
                show: false,
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "70%",
                        labels: {
                            show: true,
                            name: {
                                show: false,
                            },
                            value: {
                                fontSize: "20px",
                                fontWeight: 600,
                                formatter: (value) =>
                                    Number(value).toLocaleString(),
                            },
                            total: {
                                show: true,
                                label: "รวมทั้งหมด",
                                fontSize: "12px",
                                color: "#94a3b8",
                                formatter: (w) => {
                                    const total = w.globals.seriesTotals.reduce(
                                        (acc, val) => acc + val,
                                        0
                                    );
                                    return total.toLocaleString();
                                },
                            },
                        },
                    },
                },
            },
            tooltip: {
                theme: "light",
                y: {
                    formatter: (value) => `${value.toLocaleString()} ครั้ง`,
                },
            },
        }),
        [series]
    );

    const chartSeries = useMemo(
        () => series.map((slice) => slice.value),
        [series]
    );

    const total = useMemo(
        () => series.reduce((sum, slice) => sum + slice.value, 0),
        [series]
    );

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden">
            <h3 className="text-sm font-medium text-slate-800">{title}</h3>
            <p className="text-xs text-slate-400">{subtitle}</p>
            {remarks && <small className="text-danger text-xs">{remarks}</small>}
            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center">
                <div className="flex-1">
                    <ReactApexChart
                        options={chartOptions}
                        series={chartSeries}
                        type="donut"
                        width="100%"
                        height={300}
                    />
                </div>
                <div className="flex flex-1 flex-col gap-3 text-sm">
                    {series.map((slice) => (
                        <div
                            key={slice.label}
                            className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-block h-3.5 w-3.5 rounded-full"
                                    style={{ backgroundColor: slice.color }}
                                />
                                <span className="text-slate-600">{slice.label}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-semibold text-slate-900">
                                    {slice.value.toLocaleString()}
                                </span>
                                <span className="text-xs text-slate-400">
                                    {((slice.value / total) * 100).toFixed(1)}%
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Dashboard = () => {
    const history = useHistory();

    const getDefaultFiscalRange = () => {
        const now = moment();
        const isFiscalYearStart = now.month() >= 9;
        const startYear = isFiscalYearStart ? now.year() : now.year() - 1;
        const endYear = isFiscalYearStart ? now.year() + 1 : now.year();

        return {
            start: moment({ year: startYear, month: 9, date: 1 }),
            end: moment({ year: endYear, month: 8, date: 30 }),
        };
    };

    const [defaultRange] = useState(getDefaultFiscalRange);
    const startDate = defaultRange.start;
    const endDate = defaultRange.end;
    const [selectedLawType, setSelectedLawType] = useState(0);
    const [commentsYear, setCommentsYear] = useState(moment().year());
    const [projectYear, setProjectYear] = useState(0);
    const commentYearOptions = useMemo(() => {
        const firstYear = Math.min(startDate?.year() || moment().year(), moment().year() - 10);
        const lastYear = Math.max(endDate?.year() || moment().year(), moment().year());
        return Array.from({ length: lastYear - firstYear + 1 }, (_, index) => {
            const year = lastYear - index;
            return { value: year, label: String(year + 543) };
        });
    }, [startDate, endDate]);

    const [lawHierarchyOption, setLawHierarchyOption] = useState([
        { value: 0, label: "เลือกตามศักดิ์กฎหมาย" },
    ]);

    // API data states
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const activeRequest = useRef(null);

    // Fetch dashboard data from API
    const fetchDashboardData = useCallback(async () => {
        activeRequest.current?.abort();
        const controller = new AbortController();
        activeRequest.current = controller;
        setLoading(true);
        setError(null);

        try {
            const payload = {
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                lawType: 0,
            };

            const response = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const chartsResponse = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/overview-charts`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ comments_year: commentsYear, project_year: projectYear, lawType: selectedLawType || 0 }),
                signal: controller.signal,
            });
            if (!chartsResponse.ok) throw new Error(`HTTP error! status: ${chartsResponse.status}`);
            const charts = await chartsResponse.json();
            if (controller.signal.aborted) return;
            setDashboardData({ ...data, ...charts });
        } catch (err) {
            if (controller.signal.aborted) return;
            console.error("Error fetching dashboard data:", err);
            setError(err.message);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [startDate, endDate, selectedLawType, commentsYear, projectYear]);

    // Fetch data when filters change
    useEffect(() => {
        fetchDashboardData();
        return () => activeRequest.current?.abort();
    }, [fetchDashboardData]);

    useEffect(() => {
        getLawHierarchyOption();
    }, []);

    const getLawHierarchyOption = async () => {
        const result = await ApiLaw.getMaster();
        if (result.status == 200) {
            const { data } = result;
            const hierarchy = Array.isArray(data?.hirachy) ? data.hirachy : [];
            setLawHierarchyOption([
                { value: 0, label: "เลือกตามศักดิ์กฎหมาย" },
                ...hierarchy,
            ]);
        }
    };

    // Trigger resize after data loaded to fix chart scale
    useEffect(() => {
        if (dashboardData) {
            const timer = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [dashboardData]);

    // Helper function to get delta styling based on value
    const getDeltaStyle = (delta) => {
        if (!delta) return { text: "เมื่อเทียบปีที่ผ่านมา", class: "bg-slate-50 text-slate-600" };

        const deltaStr = String(delta).trim();

        // Check if it's zero or near zero
        if (deltaStr === "0" || deltaStr === "0%" || deltaStr === "+0" || deltaStr === "+0%" || deltaStr === "-0" || deltaStr === "-0%") {
            return { text: "ไม่เปลี่ยนแปลง", class: "bg-slate-50 text-slate-600" };
        }

        // Check if positive
        if (deltaStr.startsWith("+") || (!deltaStr.startsWith("-") && parseFloat(deltaStr) > 0)) {
            return { text: "เพิ่มขึ้นจากปีที่ผ่านมา", class: "bg-emerald-50 text-emerald-600" };
        }

        // Check if negative
        if (deltaStr.startsWith("-")) {
            return { text: "ลดลงจากปีที่ผ่านมา", class: "bg-red-50 text-red-600" };
        }

        return { text: "เมื่อเทียบปีที่ผ่านมา", class: "bg-slate-50 text-slate-600" };
    };

    const metricCards = useMemo(
        () => {
            const agencyStartedDelta = dashboardData?.agencyStarted?.delta || "+0%";
            const agencyListeningDelta = dashboardData?.agencyListening?.delta || "+0";
            const totalCommentsDelta = dashboardData?.totalComments?.delta || "+0%";
            const totalVisitsDelta = dashboardData?.totalVisits?.delta || "+0%";
            const pageviewsDelta = dashboardData?.pageviews?.delta || "+0%";
            const sessionsDelta = dashboardData?.sessions?.delta || "+0%";
            const lawListeningDelta = dashboardData?.lawListening?.delta || "+0%";
            const lawEvaluationDelta = dashboardData?.lawEvaluation?.delta || "+0%";

            return [
                {
                    category: "ภาพรวมระบบ",
                    title: "หน่วยงานที่เริ่มใช้บริการ",
                    value: dashboardData?.agencyStarted?.value || "0",
                    unit: "หน่วยงาน",
                    delta: agencyStartedDelta,
                    deltaText: getDeltaStyle(agencyStartedDelta).text,
                    deltaClass: getDeltaStyle(agencyStartedDelta).class,
                    iconBg: "bg-sky-50 text-sky-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/building-columns-duotone.svg")} />
                    ),
                    stats: dashboardData?.agencyStarted?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "ข้อมูลหน่วยงาน",
                    title: "หน่วยงานที่เปิดรับฟังความคิดเห็น",
                    value: dashboardData?.agencyListening?.value || "0",
                    unit: "หน่วยงาน",
                    delta: agencyListeningDelta,
                    deltaText: getDeltaStyle(agencyListeningDelta).text,
                    deltaClass: getDeltaStyle(agencyListeningDelta).class,
                    iconBg: "bg-indigo-50 text-indigo-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/building-columns-duotone.svg")} />
                    ),
                    link: "/dashboard/agency",
                    stats: dashboardData?.agencyListening?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "สถานะคัดเลือก",
                    title: "ปริมาณความคิดเห็น",
                    value: dashboardData?.totalComments?.value || "0",
                    unit: "ครั้ง",
                    delta: totalCommentsDelta,
                    deltaText: getDeltaStyle(totalCommentsDelta).text,
                    deltaClass: getDeltaStyle(totalCommentsDelta).class,
                    iconBg: "bg-fuchsia-50 text-fuchsia-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/poll-duotone.svg")} />
                    ),
                    stats: dashboardData?.totalComments?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "การเข้าชม",
                    title: "เข้าชมทั้งหมด",
                    value: dashboardData?.totalVisits?.value || "0",
                    unit: "ครั้ง",
                    delta: totalVisitsDelta,
                    deltaText: getDeltaStyle(totalVisitsDelta).text,
                    deltaClass: getDeltaStyle(totalVisitsDelta).class,
                    iconBg: "bg-cyan-50 text-cyan-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/poll-duotone.svg")} />
                    ),
                    stats: dashboardData?.totalVisits?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "Pageview",
                    title: "ปริมาณการเยี่ยมชม (Pageview)",
                    value: dashboardData?.pageviews?.value || "0",
                    unit: "รวม 3 ปีงบประมาณ",
                    fiscalYear: dashboardData?.pageviews?.fiscal_year,
                    delta: pageviewsDelta,
                    deltaText: getDeltaStyle(pageviewsDelta).text,
                    deltaClass: getDeltaStyle(pageviewsDelta).class,
                    iconBg: "bg-rose-50 text-rose-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/browser-duotone (3).svg")} />
                    ),
                    stats: dashboardData?.pageviews?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "Sessions",
                    title: "ปริมาณการเยี่ยมชม (Sessions)",
                    value: dashboardData?.sessions?.value || "0",
                    unit: "รวม 3 ปีงบประมาณ",
                    fiscalYear: dashboardData?.sessions?.fiscal_year,
                    delta: sessionsDelta,
                    deltaText: getDeltaStyle(sessionsDelta).text,
                    deltaClass: getDeltaStyle(sessionsDelta).class,
                    iconBg: "bg-amber-50 text-amber-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/browser-duotone (3).svg")} />
                    ),
                    stats: dashboardData?.sessions?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "ผลสัมฤทธิ์",
                    title: "จำนวนกฎหมายที่เปิดรับฟังความคิดเห็น",
                    value: dashboardData?.lawListening?.value || "0",
                    unit: "กฎหมาย (ฉบับ)",
                    delta: lawListeningDelta,
                    deltaText: getDeltaStyle(lawListeningDelta).text,
                    deltaClass: getDeltaStyle(lawListeningDelta).class,
                    iconBg: "bg-slate-100 text-violet-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/books-duotone.svg")} />
                    ),
                    link: "/dashboard/draftlaw",
                    stats: dashboardData?.lawListening?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
                {
                    category: "ผลลัพธ์",
                    title: "จำนวนกฎหมายที่ประเมินผลสัมฤทธิ์",
                    value: dashboardData?.lawEvaluation?.value || "0",
                    unit: "กฎหมาย (ฉบับ)",
                    delta: lawEvaluationDelta,
                    deltaText: getDeltaStyle(lawEvaluationDelta).text,
                    deltaClass: getDeltaStyle(lawEvaluationDelta).class,
                    iconBg: "bg-lime-50 text-lime-500",
                    icon: (
                        <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/chart-area-duotone.svg")} />
                    ),
                    link: "/dashboard/evaluate",
                    stats: dashboardData?.lawEvaluation?.stats || [
                        { label: "2566", value: "0" },
                        { label: "2567", value: "0" },
                        { label: "2568", value: "0" },
                    ],
                },
            ];
        },
        [dashboardData]
    );

    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) {
            return "ทุกช่วงเวลา";
        }
        return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    }, [startDate, endDate]);


    const monthlyCommentsChart = useMemo(() => {
        const defaultLabels = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
        const defaultValues = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        const raw = dashboardData?.commentsMonthlyChart;

        if (Array.isArray(raw)) {
            const values = [...defaultValues];
            raw.forEach((item) => {
                const month = Number(item.month_active);
                const count = Number(String(item.total_agencies ?? 0).replace(/,/g, ""));
                if (Number(item.year_active) === commentsYear && month >= 1 && month <= 12 && Number.isFinite(count)) {
                    values[month - 1] += count;
                }
            });
            return { labels: defaultLabels, values };
        }

        return {
            labels: raw?.labels || defaultLabels,
            values: raw?.values || defaultValues,
        };
    }, [dashboardData, commentsYear]);

    const barCharts = useMemo(
        () => [
            {
                title: "ความคิดเห็น (ครั้ง)",
                subtitle: "เปรียบเทียบ 2564 - 2568",
                labels: dashboardData?.commentsYearlyChart?.labels || ["2564", "2565", "2566", "2567", "2568"],
                values: dashboardData?.commentsYearlyChart?.values || [0, 0, 0, 0, 0],
                accent: "violet",
            },
            {
                title: `ความคิดเห็นรายเดือน ปี พ.ศ. ${commentsYear + 543}`,
                subtitle: "นับตามวันที่แสดงความคิดเห็น ตั้งแต่เดือนมกราคมถึงธันวาคม",
                labels: monthlyCommentsChart.labels,
                values: monthlyCommentsChart.values,
                accent: "violet",
            },
        ],
        [dashboardData, monthlyCommentsChart, commentsYear]
    );

    const chartColors = [
        "#5C6Bc0",
        "#80CBC4",
        "#90CAF9",
        "#4DD0E1",
        "#C8E6c9",
        "#f48fb1",
        "#f8bbd0",
        "#b39ddb",
        "#ce93d8",
        "#9fa8da",
    ];

    const donutCharts = useMemo(
        () => {
            // Add vibrant colors to lawTypeChart series

            const rawLawTypeChart = dashboardData?.lawTypeChart;
            let lawTypeSeries = [];

            if (Array.isArray(rawLawTypeChart)) {
                lawTypeSeries = rawLawTypeChart.map((item, index) => {
                    const rawValue = item.total_agencies ?? 0;
                    const parsedValue = Number(String(rawValue).replace(/,/g, ""));
                    return {
                        label: item.law_hierarchy_name || "ไม่ระบุ",
                        value: Number.isNaN(parsedValue) ? 0 : parsedValue,
                        color: chartColors[index % chartColors.length],
                    };
                });
            } else {
                lawTypeSeries = rawLawTypeChart?.series?.map((item, index) => ({
                    ...item,
                    color: item.color || chartColors[index % chartColors.length]
                })) || [];
            }


            if (!lawTypeSeries.length) {
                lawTypeSeries = [
                    { label: "รัฐธรรมนูญ", value: 0, color: chartColors[0] },
                    { label: "พระราชบัญญัติ", value: 0, color: chartColors[1] },
                    { label: "พระราชกฤษฎีกา", value: 0, color: chartColors[2] },
                    { label: "พระราชกำหนด", value: 0, color: chartColors[3] },
                    { label: "กฎกระทรวง", value: 0, color: chartColors[4] },
                    { label: "ประกาศ", value: 0, color: chartColors[5] },
                    { label: "อื่นๆ", value: 0, color: chartColors[6] },
                ];
            }

            return [
                {
                    title: `ช่วงวันที่ ${rangeLabel} : การรับฟังแยกตามประเภทศักดิ์กฎหมาย`,
                    subtitle: "กฎหมายหลัก/รอง",
                    series: lawTypeSeries,
                    remarks: `หมายเหตุ: ค่า "ไม่ระบุ" หมายถึง ข้อมูลการรับฟังร่างกฎหมายลำดับรองจากชุดข้อมูลเดิม ซึ่งยังไม่มีการแยกลำดับศักดิ์กฎหมาย จึงไม่สามารถระบุลำดับศักดิ์ได้`
                },
                {
                    title: `ช่วงวันที่ ${rangeLabel} : สัดส่วนการรับฟังความคิดเห็น และการรับฟังประเมินผลสัมฤทธิ์`,
                    subtitle: "ผลลัพธ์การดำเนินงาน",
                    series: dashboardData?.listeningVsEvaluationChart?.series || [
                        { label: "เข้ารับฟังความคิดเห็น", value: 0, color: "#438940" },
                        { label: "ประเมินผลสัมฤทธิ์", value: 0, color: "#ef4444" },
                    ],
                },
            ];
        },
        [dashboardData, rangeLabel]
    );

    // Loading state
    if (loading && !dashboardData) {
        return <DashboardSkeleton variant="overview" />;
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="text-red-500 mb-4">
                                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-sm text-slate-500">เกิดข้อผิดพลาด: {error}</p>
                            <button
                                onClick={fetchDashboardData}
                                className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm hover:bg-sky-600"
                            >
                                ลองใหม่อีกครั้ง
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const csvEscape = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value);
        if (/[",\n]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
    };


    const handleDownloadExcel = () => {
        const selectedCards = [
            metricCards[1], // หน่วยงานที่เปิดรับฟังความคิดเห็น
            metricCards[2], // ปริมาณความคิดเห็น
            metricCards[4], // ปริมาณการเยี่ยมชม (Pageview)
            metricCards[5], // ปริมาณการเยี่ยมชม (Sessions)
        ];

        const allLabels = [...new Set(selectedCards.flatMap((c) => (c.stats || []).map((s) => s.label)))];
        allLabels.sort((a, b) => Number(a) - Number(b));
        const headerRow = ["ลำดับ", "รายการ", "ค่ารวม", "หน่วย", ...allLabels.map((l) => `ปี ${l}`)];

        const dataRows = selectedCards.map((card, idx) => {
            const statsMap = new Map((card.stats || []).map((s) => [s.label, s.value]));
            return [
                idx + 1,
                card.title,
                card.value,
                card.unit,
                ...allLabels.map((l) => statsMap.get(l) ?? ""),
            ];
        });

        const rows = [headerRow, ...dataRows];
        const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
        const blob = new Blob(["\ufeff", csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `summary-dashboard-${moment().format(
            "YYYYMMDD"
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24" aria-busy={loading}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-1">
                    <div className="dashboard-overview-header">
                        <button
                            onClick={() => history.go(-1)}
                            className="dashboard-overview-header__back inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-1 text-xs font-medium text-sky-600 hover:bg-slate-50"
                        >
                            <svg viewBox="0 0 24 24" className="h-4 w-4">
                                <path
                                    d="M15 18l-6-6 6-6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            ย้อนกลับ
                        </button>
                        <h1 className="dashboard-overview-header__title text-primary text-semibold">สถิติการใช้งานระบบกลางทางกฎหมาย (Law Portal): สถิติภาพรวม</h1>
                        <button
                            type="button"
                            onClick={handleDownloadExcel}
                            className="dashboard-overview-header__download whitespace-nowrap inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        >
                            ดาวน์โหลด Excel
                        </button>
                        <p className="dashboard-overview-header__note text-sm text-primary">หมายเหตุ: การ์ดข้อมูลรายปีใช้ปีงบประมาณ 1 ตุลาคม - 30 กันยายน โดย Pageviews / Sessions รวม 3 ปีงบประมาณล่าสุด และปีปัจจุบันนับถึงวันนี้ ส่วนกราฟด้านล่างใช้ปีปฏิทิน</p>
                        {loading && (
                            <span role="status" className="dashboard-overview-header__note text-xs text-slate-400">กำลังโหลด...</span>
                        )}
                    </div>
                </section>

                <MonthlyReportExport />

                <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {[metricCards[1], metricCards[2], metricCards[4], metricCards[5]].map((card) => (
                        <MetricCard key={card.title} item={card} />
                    ))}
                    {/* {metricCards.slice(4).map((card) => (
                        <MetricCard key={card.title} item={card} />
                    ))} */}
                </section>

                <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
                    {metricCards.slice(6).map((card) => (
                        <MetricCard key={card.title} item={card} />
                    ))}
                </section>

                <section className="space-y-3">
                    <div className="flex justify-end">
                        <FilterSelect label="เลือกปี พ.ศ. สำหรับความคิดเห็นรายเดือน" options={commentYearOptions} value={commentsYear} onChange={setCommentsYear} />
                    </div>
                    {barCharts.slice(1).map((chart) => (
                        <BarChartCard key={chart.title} barColors={chartColors} {...chart} />
                    ))}
                </section>

                <OverviewProjectCharts data={dashboardData} year={projectYear} onYearChange={setProjectYear}
                    lawType={selectedLawType} lawOptions={lawHierarchyOption} onLawTypeChange={setSelectedLawType} />
            </div>
        </div>
    );
};

export default Dashboard;
